import { getProductType, ProductEnum } from "../../constants/login";

export const DEFAULT_IMPORT_MODULE_NAME = "Settings";
export const SPA_TRANSACTIONS_MODULE_NAME = "Transactions";
export const GOLF_MODULE_NAME = "Golf";
/** Retail Items Setup / Retail sheet — Spa, Golf, SnC. */
export const RETAIL_MODULE_NAME = "Retail";

/** Spa excel file names that use ImportExcelData moduleName "Transactions". */
export const SPA_TRANSACTIONS_EXCEL_FILE_NAMES = new Set([
  "Therapist",
  "ServiceDetail",
  "PackageDetail",
]);

/** Golf sheets that import via moduleName "Golf" (not Settings). */
export const GOLF_MODULE_EXCEL_FILE_NAMES = new Set([
  "PlayerTypeMapping",
  "RateTypeMapping",
]);

/** Golf excel file names that use ImportExcelData moduleName "Transactions". */
export const GOLF_TRANSACTIONS_EXCEL_FILE_NAMES = new Set([
  "RatePlan",
  "CourseTeeFee",
]);

/** Files that import via moduleName "Retail" (Spa/Golf/SnC RetailItem). */
export const RETAIL_MODULE_EXCEL_FILE_NAMES = new Set(["RetailItem"]);

/** Removes generated timestamp suffix from exported .xlsx names. */
export const stripGeneratedExcelTimestamp = (fileName: string): string =>
  fileName.replace(/\.xlsx$/i, "").replace(/_\d{2}-\d{2}-\d{4}_\d{6}$/i, "");

/**
 * Multi-tab workbooks share one export fileName. ImportExcelData must use a
 * unique name per sheet or Visual One may record a duplicate failed request.
 */
export const buildPerSheetImportFileName = (
  workbookFileName: string,
  excelSheetName: string,
): string => {
  const sheetBase = String(excelSheetName || "")
    .replace(/[<>:"/\\|?*]/g, "_")
    .trim();
  if (!sheetBase) {
    return workbookFileName;
  }

  const stampMatch = workbookFileName.match(
    /(_\d{2}-\d{2}-\d{4}_\d{6})(\.xlsx)$/i,
  );
  if (stampMatch) {
    return `${sheetBase}${stampMatch[1]}${stampMatch[2]}`;
  }

  return `${sheetBase}.xlsx`;
};

export const resolveImportModuleName = (
  productId: number,
  excelFileName: string,
): string => {
  const product = getProductType(productId) as ProductEnum | undefined;
  const baseName = stripGeneratedExcelTimestamp(excelFileName);

  if (RETAIL_MODULE_EXCEL_FILE_NAMES.has(baseName)) {
    if (
      product === ProductEnum.SPA ||
      product === ProductEnum.GOLF ||
      product === ProductEnum.SALES_AND_CATETING
    ) {
      return RETAIL_MODULE_NAME;
    }
  }

  if (product === ProductEnum.SPA) {
    return SPA_TRANSACTIONS_EXCEL_FILE_NAMES.has(baseName)
      ? SPA_TRANSACTIONS_MODULE_NAME
      : DEFAULT_IMPORT_MODULE_NAME;
  }

  if (product === ProductEnum.GOLF) {
    if (GOLF_TRANSACTIONS_EXCEL_FILE_NAMES.has(baseName)) {
      return SPA_TRANSACTIONS_MODULE_NAME;
    }
    return GOLF_MODULE_EXCEL_FILE_NAMES.has(baseName)
      ? GOLF_MODULE_NAME
      : DEFAULT_IMPORT_MODULE_NAME;
  }

  return DEFAULT_IMPORT_MODULE_NAME;
};

/** Visual One expects plain `{FileName}.xlsx` for module-specific imports. */
export const resolveImportApiFileName = (
  excelFileName: string,
  moduleName: string,
): string => {
  const withExt = excelFileName.toLowerCase().endsWith(".xlsx")
    ? excelFileName
    : `${excelFileName}.xlsx`;

  if (
    moduleName === SPA_TRANSACTIONS_MODULE_NAME ||
    moduleName === GOLF_MODULE_NAME ||
    moduleName === RETAIL_MODULE_NAME
  ) {
    return `${stripGeneratedExcelTimestamp(withExt)}.xlsx`;
  }

  return withExt;
};

export type ImportDataUtilityRow = {
  importData: string;
  totalRows: number;
  excelSheetName: string;
};

/** Transactions payload uses totalRows 0 on each dataUtilityImportData item. */
export const normalizeImportDataUtilityItems = <T extends ImportDataUtilityRow>(
  items: T[],
  moduleName: string,
): T[] => {
  if (moduleName !== SPA_TRANSACTIONS_MODULE_NAME) {
    return items;
  }

  return items.map((item) => ({ ...item, totalRows: 0 }));
};
